import * as Types from '../../declarations'
import React from 'react'
import { Text, Title } from '@mantine/core'
import { Chart, ChartType, ScriptableScaleContext } from 'chart.js/auto'
import * as AppStore from './../../state/app-store'
import * as Selectors from './../../state/selectors'
import SectionHeader from './components/SectionHeader'
import HTMLContent from 'components/HTMLContent'
import { trekkingColours, peaksColours } from 'pages/_app'

interface Props {
	trip: Types.DB.Models.TripAttributes
}

const Weather = ({
	trip: {
		name,
		jsonData: { weather, highLevelInfo, trekOrClimb },
	},
}: Props) => {
	if (!weather) return <></>

	const canvasEl = React.useRef<HTMLCanvasElement>(null)

	const bestMonths = highLevelInfo?.whenToGo?.bestMonths ?? []

	const unit = AppStore.useAppSelector(Selectors.unitSelector)

	// todo: use nicer colours, these are ugly ass
	const colors = {
		temperature: {
			default: peaksColours[9], //'rgba(240, 214, 150, 1)', // #f0d696
			goodMonth: peaksColours[0], //'rgba(204, 85, 0, 1)', // #cc5500
		},
		rainfall: {
			default: trekkingColours[5], //'rgba(126, 183, 191, 1)', // #7eb7bf
			goodMonth: trekkingColours[8], //'rgba(83, 115, 76, 1)', // #53734c
		},
	}

	const months = [
		'Jan',
		'Feb',
		'Mar',
		'Apr',
		'May',
		'Jun',
		'Jul',
		'Aug',
		'Sep',
		'Oct',
		'Nov',
		'Dec',
	]

	const isTrek = trekOrClimb === Types.App.DataTypes.TrekOrClimb.TREK

	React.useEffect(() => {
		if (!canvasEl?.current) return

		const ctx = canvasEl.current.getContext('2d')

		if (!ctx) return

		// const gradient = ctx.createLinearGradient(0, 16, 0, 100)
		// gradient.addColorStop(0, colors.temperature.half)
		// gradient.addColorStop(0.65, colors.temperature.quarter)
		// gradient.addColorStop(1, colors.temperature.zero)

		const labels = months
		// todo: make this context aware eg units
		const data = {
			labels: labels,
			datasets: [
				{
					type: 'line' as ChartType,
					label: 'Temperature',
					data: (weather?.monthlyWeatherData ?? []).map(({ temperature }) =>
						unit === 'metric' ? temperature : (temperature * 9) / 5 + 32,
					),
					borderWidth: 2,
					lineTension: 0.2,
					pointBackgroundColor: labels.map((_, index) =>
						bestMonths.includes(index + 1)
							? colors.temperature.goodMonth
							: colors.temperature.default,
					),

					borderColor: labels.map((_, index) =>
						bestMonths.includes(index + 1)
							? colors.temperature.goodMonth
							: colors.temperature.default,
					),
					borderDash: [10],
					pointRadius: 3,
					yAxisID: 'temperature',
					tooltip: {
						callbacks: {
							// @ts-ignore
							label: (item) =>
								`${item.dataset.label}: ${item.formattedValue} °${
									unit === 'metric' ? 'C' : 'F'
								}`,
						},
					},
				},
				{
					type: 'bar' as ChartType,
					label: 'Rainfall',
					data: (weather?.monthlyWeatherData ?? []).map(({ rainfall }) =>
						unit === 'metric' ? rainfall : rainfall / 25.4,
					),
					borderColor: labels.map((_, index) =>
						bestMonths.includes(index + 1)
							? colors.rainfall.goodMonth
							: colors.rainfall.default,
					),
					fill: true,
					borderWidth: 2,
					backgroundColor: labels.map((_, index) =>
						bestMonths.includes(index + 1)
							? colors.rainfall.goodMonth
							: colors.rainfall.default,
					),
					lineTension: 0.2,
					pointRadius: 3,
					yAxisID: 'rainfall',
					tooltip: {
						callbacks: {
							// @ts-ignore
							label: (item) =>
								`${item.dataset.label}: ${item.formattedValue} ${
									unit === 'metric' ? 'mm' : 'inches'
								}`,
						},
					},
				},
			],
		}

		const config = {
			data: data,
			options: {
				scales: {
					temperature: {
						type: 'linear',
						position: 'left',
						ticks: { beginAtZero: true, color: colors.temperature.default },
						// Hide grid lines, otherwise you have separate grid lines for the 2 y axes
						grid: { display: false },
						stacked: false,
						// @ts-ignore
						ticks: {
							// Include a dollar sign in the ticks
							callback: (value: string) =>
								value + ' °' + (unit === 'metric' ? 'C' : 'F'),
						},
					},
					rainfall: {
						type: 'linear',
						position: 'right',
						ticks: { beginAtZero: true, color: colors.rainfall.default },
						grid: { display: false },
						stacked: false,
						// @ts-ignore
						ticks: {
							// Include a dollar sign in the ticks
							callback: (value: string) =>
								value + (unit === 'metric' ? ' mm' : ' inches'),
						},
					},
					x: {
						ticks: {
							color: function (context: ScriptableScaleContext) {
								if (bestMonths.includes(context.tick.value + 1)) {
									return 'rgba(0, 0, 0, 1)'
								}
								return 'rgba(0, 0, 0, .3)'
							},
							// todo: does this do anything?
							backdropColor: function (context: ScriptableScaleContext) {
								if (bestMonths.includes(context.tick.value + 1)) {
									return 'rgba(25, 190, 25, 1)'
								}
								return '#000000'
							},
						},
					},
				},
			},
		}

		// todo: fix type 'issue'
		// @ts-ignore
		const myLineChart = new Chart(ctx, config)

		return function cleanup() {
			myLineChart.destroy()
		}
	}, [unit])

	return (
		<div id="weather-wrapper" className="spacing-between-sections-above">
			<SectionHeader
				title={`${name} weather `}
				subtitle={`How is the weather on ${name}, and when is the best time to go.`}
				section={Types.App.DataTypes.FeedbackSection.WEATHER}
			/>
			<Text mb="xl">
				<HTMLContent html={weather.description}></HTMLContent>
			</Text>

			<canvas id="myChart" ref={canvasEl} height="100" />
			{bestMonths.length > 0 && (
				<Text mb="xl">
					<Title order={3}>
						The best time to {isTrek ? 'trek' : 'climb'} {name} is in these
						months:
					</Title>
					<ul>
						{bestMonths.map((monthNumber, ind) => (
							<li key={ind}>{months[monthNumber - 1]}</li>
						))}
					</ul>
				</Text>
			)}

			{highLevelInfo?.whenToGo?.note && (
				<Text mb="xl">{highLevelInfo.whenToGo.note}</Text>
			)}
		</div>
	)
}

export default Weather
